import {
  CardID,
  DateISO,
  EventID,
  ListID,
  TripCard,
  TripDay,
  TripEvent,
  TripList,
} from '../redux/trip/trip.types';
import { getList } from '../redux/trip/trip.utils';
import { Marker, MarkerType } from '../types/MapMarker';

function isMarker(markerOrUndefined: Marker | undefined): markerOrUndefined is Marker {
  return markerOrUndefined !== undefined;
}

function isMarkerArray(
  markerArrayOrUndefined: (Marker | undefined)[] | undefined
): markerArrayOrUndefined is (Marker | undefined)[] {
  return markerArrayOrUndefined !== undefined;
}

export function getMarkersFromCardsAndExtendBounds(
  cards: TripCard[],
  lists: TripList[],
  cardIdToListId: { [key: CardID]: ListID },
  bounds: google.maps.LatLngBounds,
  highlightedCardId?: string | null
) {
  return cards
    .map((card) => {
      if (card.location) {
        const cardLocation = {
          lat: card.location.latitude,
          lng: card.location.longitude,
        };
        bounds.extend(cardLocation);
        const listId = cardIdToListId[card.id];
        const list = getList(lists, listId);
        return {
          id: card.id,
          markerType: MarkerType.EMOJI,
          location: cardLocation,
          data: {
            emoji: list?.emoji,
            description: card.customName ?? card.name,
            isHighlighted: card.id === highlightedCardId,
          },
        } as Marker;
      }
      return undefined;
    })
    .filter(isMarker);
}

export function getMarkersForCardsInListsAndExtendBounds(
  lists: TripList[],
  cards: { [key: CardID]: TripCard },
  bounds: google.maps.LatLngBounds,
  highlightedCardId?: string | null
) {
  let didBoundsUpdate = false;
  const markers = lists
    .map((list) =>
      list.cardOrder?.map((cardId) => {
        const card = cards[cardId];
        if (card && card.location) {
          const cardLocation = {
            lat: card.location.latitude,
            lng: card.location.longitude,
          };
          bounds.extend(cardLocation);
          didBoundsUpdate = true;
          return {
            id: card.id,
            markerType: MarkerType.EMOJI,
            location: cardLocation,
            data: {
              emoji: list.emoji,
              description: card.customName ?? card.name,
              isHighlighted: card.id === highlightedCardId,
            },
          } as Marker;
        }
        return undefined;
      })
    )
    .filter(isMarkerArray)
    .reduce((previousValue, currentValue) => previousValue?.concat(currentValue), [])
    .filter(isMarker);

  return { markers, didBoundsUpdate };
}

// Resolves selectedDayDateISOs → day.eventOrder → events → cards to build markers for all places scheduled on the selected itinerary days, extending bounds to fit them.
export function getMarkersForDaysAndExtendBounds(
  selectedDayDateISOs: DateISO[],
  days: { [key: DateISO]: TripDay },
  events: { [key: EventID]: TripEvent },
  cards: { [key: CardID]: TripCard },
  cardIdToEmoji: Record<CardID, string>,
  bounds: google.maps.LatLngBounds
): { markers: Marker[]; didBoundsUpdate: boolean } {
  let didBoundsUpdate = false;
  const markers: Marker[] = [];

  for (const dateISO of selectedDayDateISOs) {
    const day = days[dateISO];
    if (!day?.eventOrder) {
      continue;
    }

    for (const eventId of day.eventOrder) {
      const event = events[eventId];
      if (!event) {
        continue;
      }
      const card = cards[event.cardId];
      if (!card?.location) {
        continue;
      }

      const cardLocation = { lat: card.location.latitude, lng: card.location.longitude };
      bounds.extend(cardLocation);
      didBoundsUpdate = true;

      markers.push({
        id: card.id,
        markerType: MarkerType.EMOJI,
        location: cardLocation,
        data: {
          emoji: cardIdToEmoji[card.id] ?? '📍',
          description: card.customName ?? card.name ?? '',
          isHighlighted: false,
        },
      });
    }
  }

  return { markers, didBoundsUpdate };
}
